/*
 * Карточка шаблона в разделе «Создать» (проп TemplateCardComponent у ScaffolderPage):
 * шапка оттенка типа с плашкой-иконкой и водяным знаком технологии в углу с нахлёстом,
 * ниже — описание, тэги, ссылки, владелец и «Выбрать», как у стандартной карточки.
 * Оформление по типу — lib/templateTypes.
 */
import { CSSProperties, ReactNode, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Grid,
  Typography,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import LanguageIcon from '@material-ui/icons/Language';
import { RELATION_OWNED_BY } from '@backstage/catalog-model';
import { Link, MarkdownContent } from '@backstage/core-components';
import { useAnalytics, useApp } from '@backstage/core-plugin-api';
import {
  EntityRefLinks,
  FavoriteEntity,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import { TemplateCardComponentProps } from '@backstage/plugin-scaffolder-react/alpha';
import { templateLook } from '../../lib/templateTypes';

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  head: {
    position: 'relative',
    minHeight: 124,
    padding: theme.spacing(2.25, 2.5),
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  glyph: {
    position: 'absolute',
    right: -26,
    top: -20,
    lineHeight: 0,
    pointerEvents: 'none',
    '& svg': { width: 132, height: 132 },
  },
  row: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  icbox: {
    width: 44,
    height: 44,
    borderRadius: 14,
    flex: '0 0 44px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    boxShadow: '0 6px 16px -8px rgba(0,0,0,.35)',
  },
  grow: { flex: 1, minWidth: 0 },
  type: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  title: {
    margin: '4px 0 0',
    fontSize: 18,
    fontWeight: 800,
    letterSpacing: '-0.01em',
    lineHeight: 1.25,
    paddingRight: 56,
  },
  content: { flex: 1, paddingBottom: 0 },
  description: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 10,
    '-webkit-box-orient': 'vertical',
    '& :first-child': { margin: 0 },
  },
  tags: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(0.75) },
  links: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    fontSize: 13,
  },
  link: { display: 'inline-flex', alignItems: 'center', gap: 6 },
  actions: { padding: theme.spacing(2, 2.5, 2.25), alignItems: 'center' },
  owner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flex: 1,
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
}));

const useThemeMode = (): 'light' | 'dark' => {
  const { palette } = useTheme();
  return palette.type === 'dark' ? 'dark' : 'light';
};

/** Шапка: тонировка и цвет типа по теме, водяной знак в углу. */
const ThemedHead = ({
  look,
  classes,
  children,
}: {
  template: TemplateEntityV1beta3;
  look: ReturnType<typeof templateLook>;
  classes: ReturnType<typeof useStyles>;
  children: ReactNode;
}) => {
  const mode = useThemeMode();
  const color = look.color[mode];
  const { Icon } = look;
  return (
    <div
      className={classes.head}
      style={
        {
          background: look.tint[mode],
          '--viva-type-color': color,
        } as CSSProperties
      }
    >
      <span
        className={classes.glyph}
        style={{ color, opacity: mode === 'dark' ? 0.22 : 0.16 }}
      >
        <Icon />
      </span>
      {children}
    </div>
  );
};

export const VivaTemplateCard = ({
  template,
  additionalLinks,
  onSelected,
}: TemplateCardComponentProps) => {
  const classes = useStyles();
  const app = useApp();
  const analytics = useAnalytics();
  const type = template.spec.type;
  const look = templateLook(type);
  const { Icon } = look;
  const owners = getEntityRelations(template, RELATION_OWNED_BY);
  const tags = template.metadata.tags ?? [];
  const links = [
    ...(template.metadata.links ?? []).map(l => ({
      text: l.title ?? l.url,
      url: l.url,
      Icon: (l.icon && app.getSystemIcon(l.icon)) || LanguageIcon,
    })),
    ...(additionalLinks ?? []).map(l => ({
      text: l.text,
      url: l.url,
      Icon: l.icon,
    })),
  ];

  const choose = useCallback(() => {
    analytics.captureEvent('click', 'Template has been opened');
    // TemplateGroup передаёт своей карточке обработчик списка без привязки к шаблону
    // (стандартной — уже привязанный), поэтому шаблон передаём сами: лишний аргумент
    // привязанному обработчику не мешает
    (onSelected as ((t?: TemplateEntityV1beta3) => void) | undefined)?.(
      template,
    );
  }, [analytics, onSelected, template]);

  return (
    <Card className={classes.card} data-testid="template-card">
      <ThemedHead template={template} look={look} classes={classes}>
        <div className={classes.row}>
          <span
            className={classes.icbox}
            style={{ background: 'var(--viva-type-color)' }}
          >
            <Icon />
          </span>
          <div className={classes.grow}>
            <div className={classes.type}>
              <span>{type}</span>
              <FavoriteEntity entity={template} style={{ padding: 0 }} />
            </div>
            <Typography component="h3" className={classes.title}>
              {template.metadata.title ?? template.metadata.name}
            </Typography>
          </div>
        </div>
      </ThemedHead>
      <CardContent className={classes.content}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box className={classes.description}>
              <MarkdownContent
                content={template.metadata.description ?? 'Без описания'}
              />
            </Box>
          </Grid>
          {tags.length > 0 && (
            <Grid item xs={12}>
              <div className={classes.tags}>
                {tags.map(tag => (
                  <Chip key={tag} size="small" label={tag} />
                ))}
              </div>
            </Grid>
          )}
          {links.length > 0 && (
            <Grid item xs={12}>
              <div className={classes.links}>
                {links.map(l => (
                  <Link key={l.url} to={l.url} className={classes.link}>
                    <l.Icon fontSize="small" />
                    {l.text}
                  </Link>
                ))}
              </div>
            </Grid>
          )}
        </Grid>
      </CardContent>
      <CardActions className={classes.actions}>
        <div className={classes.owner}>
          {owners.length > 0 && (
            <>
              <PersonOutlineIcon fontSize="small" />
              <EntityRefLinks
                entityRefs={owners}
                defaultKind="Group"
                hideIcons
              />
            </>
          )}
        </div>
        <Button
          size="small"
          variant="contained"
          color="primary"
          data-testid="template-card-actions--create"
          onClick={choose}
        >
          Выбрать
        </Button>
      </CardActions>
    </Card>
  );
};
